import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { hexToCSSFilter } from "hex-to-css-filter";

import useMediaQuery from "@mui/material/useMediaQuery";

import menuFooter from "../assets/footer/Menu-footer.svg";
import ordersFooter from "../assets/footer/Orders-footer.svg";
import MenuFSR from "../assets/images/FSR-Icons/Menu-FSR.svg";
import PayBillFSR from "../assets/images/FSR-Icons/PayBill-FSR.svg";
import PayBillNotActive from "../assets/images/FSR-Icons/paybill-notactive.svg";
import coloredOrders from "../assets/images/FSR-Icons/coloredOrders.svg";

// PayBill — outline-only inline SVG, always colored via prop, never filled
const PayBillIcon = ({ color, opacity }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ opacity, transition: "opacity 0.2s ease-in-out" }}
  >
    {/* Card body */}
    <rect
      x="3"
      y="5"
      width="18"
      height="14"
      rx="2"
      stroke={color}
      strokeWidth="1.8"
      fill="none"
    />
    {/* Magnetic Stripe */}
    <line
      x1="3"
      y1="9.5"
      x2="21"
      y2="9.5"
      stroke={color}
      strokeWidth="1.8"
    />
    {/* Chip */}
    <rect
      x="6"
      y="13"
      width="4"
      height="3"
      rx="0.5"
      stroke={color}
      strokeWidth="1.5"
      fill="none"
    />
    {/* Dots */}
    <line
      x1="12"
      y1="14.5"
      x2="18"
      y2="14.5"
      stroke={color}
      strokeWidth="1.5"
      strokeDasharray="1.5 2"
    />
  </svg>
);

const DiningFooter = function () {
  const navigate = useNavigate();
  const location = useLocation();

  const storeInfoConfig = useSelector(state => state.sessionReducer?.store_config);
  const isTableMgmt = useSelector(state => state.sessionReducer?.is_fsr === true);
  const tableDetails = useSelector(state => state.sessionReducer?.table_details) || {};
  const location_id = useSelector(state => state.sessionReducer?.location_id);
  const last_order = useSelector(state => state.customerReducer?.last_order);

  const currentPath = location.pathname;
  const visiblePaths = ["/menu", "/menu-items", "/cart", "/order-status"];
  const shouldShow = isTableMgmt && visiblePaths.includes(currentPath);

  // Add/remove body class so CSS can push fixed buttons above footer
  useEffect(() => {
    if (shouldShow) {
      document.body.classList.add("dining-footer-active");
    } else {
      document.body.classList.remove("dining-footer-active");
    }
    return () => {
      document.body.classList.remove("dining-footer-active");
    };
  }, [shouldShow]);

  // Set --dining-footer-height CSS variable so any page (including direct /menu load)
  // can use calc(var(--dining-footer-height, 0px) + Xpx) for correct bottom padding
  useEffect(() => {
    const height = shouldShow ? "calc(64px + env(safe-area-inset-bottom, 0px))" : "0px";
    document.documentElement.style.setProperty("--dining-footer-height", height);
    return () => {
      document.documentElement.style.setProperty("--dining-footer-height", "0px");
    };
  }, [shouldShow]);

  if (!shouldShow) return null;

  const primaryColor =
    storeInfoConfig?.general_config?.primary_color || "#272727";

  const tableNo = (() => {
    if (isTableMgmt) {
      if (tableDetails?.qr_table_id) return tableDetails.qr_table_id;
      const fullTableId = (tableDetails?.floor_name || "") + (tableDetails?.table_name || "");
      if (fullTableId) return fullTableId;
      if (tableDetails?.floor_no && tableDetails?.table_name) return `F${tableDetails.floor_no}T${tableDetails.table_name}`;
      return (tableDetails?.table_no || "").replace(/^table_|^tent_/i, "");
    }
    const tent = tableDetails?.table_no || "";
    const rawTable = tent || location_id || "";
    return String(rawTable).replace(/^table_|^tent_/i, "");
  })();

  const normalizeTableId = (value) =>
    String(value || "").replace(/^table_|^tent_/i, "").replace(/\s+/g, "").toUpperCase();

  const currentTableIds = (() => {
    try {
      return [
        tableDetails?.qr_table_id,
        tableDetails?.floor_name && tableDetails?.table_name ? `${tableDetails.floor_name}${tableDetails.table_name}` : "",
        tableDetails?.floor_no && tableDetails?.table_no ? `F${tableDetails.floor_no}T${tableDetails.table_no}` : "",
        tableDetails?.table_no,
        tableNo,
      ].map(normalizeTableId).filter(Boolean);
    } catch (e) {}
    return [tableNo].map(normalizeTableId).filter(Boolean);
  })();

  const isCurrentTableOrder = (order) => {
    const orderTableIds = [
      order?.store_details?.table_no,
      order?.table_no,
      order?.store_details?.floor_no && order?.store_details?.table_no
        ? `F${order.store_details.floor_no}T${order.store_details.table_no}`
        : "",
      order?.floor_no && order?.table_no ? `F${order.floor_no}T${order.table_no}` : "",
    ].map(normalizeTableId).filter(Boolean);

    return orderTableIds.some((orderTableId) => currentTableIds.includes(orderTableId));
  };

  const isOpenOrder = (order) => {
    const status = (order?.order_status || "").toUpperCase();
    const state = (order?.order_state || "").toUpperCase();
    const isClosed =
      status === "CLOSE" ||
      status === "CLOSED" ||
      status === "COMPLETED" ||
      status === "COMPLETE" ||
      status === "CANCELLED" ||
      state === "CLOSE" ||
      state === "CLOSED" ||
      state === "COMPLETED" ||
      state === "COMPLETE" ||
      state === "CANCELLED";
    return status === "OPEN" && !isClosed;
  };

  const hasActiveOrderInRedux = (() => {
    const orderDetails = last_order?.order_details;
    if (!Array.isArray(orderDetails) || !orderDetails.length) return false;
    return orderDetails.some((order) => isCurrentTableOrder(order) && isOpenOrder(order));
  })();

  const payBillEnabled = (() => {
    if (hasActiveOrderInRedux) return true;
    if (!tableNo) return false;
    return localStorage.getItem("PAY_BILL_ENABLED_" + tableNo) === "true";
  })();

  const handleTabClick = (tab) => {
    if (tab === "menu") {
      navigate("/menu");
    } else if (tab === "orders") {
      navigate("/cart", { state: { fsrView: "orders", activeTab: "orders" } });
    } else if (tab === "pay") {
      if (!payBillEnabled) return;
      navigate("/cart", { state: { fsrView: "orders", activeTab: "pay" } });
    }
  };

  const isTabActive = (tab) => {
    if (tab === "menu") return currentPath === "/menu" || currentPath === "/menu-items";
    if (tab === "orders") {
      return currentPath === "/cart" && location.state?.fsrView === "orders" && location.state?.activeTab === "orders";
    }
    if (tab === "pay") {
      return currentPath === "/cart" && location.state?.fsrView === "orders" && location.state?.activeTab === "pay";
    }
    return false;
  };

  // Compute CSS filter: brightness(0) turns the SVG black, then colorize to primary
  let primaryFilter = "";
  try {
    const filterRes = hexToCSSFilter(primaryColor);
    primaryFilter = `brightness(0) ${filterRes.filter}`;
  } catch (error) {
    console.error("Error computing CSS filter for footer icons:", error);
  }

  const tabStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flex: 1,
    padding: "10px 0 8px 0",
    height: "64px",
    boxSizing: "border-box",
  };

  const getImgStyle = (tab) => ({
    width: "24px",
    height: "24px",
    filter: primaryFilter,
    opacity: isTabActive(tab) ? 1 : 0.45,
    transition: "opacity 0.2s ease-in-out",
    objectFit: "contain",
  });

  const labelStyle = {
    fontSize: "11px",
    marginTop: "4px",
    fontWeight: "600",
    fontFamily: "Satoshi, sans-serif",
    color: "#333333",
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        height: "calc(64px + env(safe-area-inset-bottom, 0px))",
        backgroundColor: "#ffffff",
        borderTop: "1px solid #e2e8f0",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "flex-start",
        paddingTop: "0px",
        zIndex: 1100,
        boxShadow: "0px -2px 10px rgba(0, 0, 0, 0.08)",
        boxSizing: "border-box",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        /* iOS scroll jitter fix: force GPU layer so the bar
           doesn't bounce during rubber-band scrolling */
        WebkitTransform: "translateZ(0)",
        transform: "translateZ(0)",
        willChange: "transform",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
      }}
    >
      {/* Menu Tab */}
      <div style={tabStyle} onClick={() => handleTabClick("menu")}>
        {isTabActive("menu") ? (
          <img src={MenuFSR} style={getImgStyle("menu")} alt="Menu" />
        ) : (
          <img src={menuFooter} style={getImgStyle("menu")} alt="Menu" />
        )}
        <span style={labelStyle}>Menu</span>
      </div>

      {/* Orders Tab */}
      <div style={tabStyle} onClick={() => handleTabClick("orders")}>
        {isTabActive("orders") ? (
          <img src={coloredOrders} style={getImgStyle("orders")} alt="Orders" />
        ) : (
          <img src={ordersFooter} style={getImgStyle("orders")} alt="Orders" />
        )}
        <span style={labelStyle}>Orders</span>
      </div>

      {/* Pay Bill Tab */}
      <div
        style={{
          ...tabStyle,
          opacity: payBillEnabled ? 1 : 0.35,
          cursor: payBillEnabled ? "pointer" : "not-allowed"
        }}
        onClick={() => handleTabClick("pay")}
      >
        {isTabActive("pay") ? (
          <img src={PayBillFSR} style={getImgStyle("pay")} alt="Pay Bill" />
        ) : (
          <img src={PayBillNotActive} style={getImgStyle("pay")} alt="Pay Bill" />
        )}
        <span style={labelStyle}>Pay Bill</span>
      </div>
    </div>
  );
};

export default DiningFooter;
